import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as socketService from '../services/socket';
import ResumeSessionCard from '../components/ResumeSessionCard';

interface JoinLocationState {
  /** Error reported by the room page after a failed join (room gone, name taken, ...). */
  error?: string;
  /** Username the player tried to join with, so they don't have to retype it. */
  username?: string;
}

/**
 * Collects a username (and room code, unless it came from an invite link) and hands off to the
 * Room page, which owns the actual socket join. Join failures come back here through navigation
 * state so they can be shown inline.
 */
const Join: React.FC = () => {
  const { roomId: roomIdParam } = useParams<{ roomId?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = (location.state as JoinLocationState | null) || null;

  const invitedRoomId = socketService.normalizeRoomId(
    roomIdParam || searchParams.get('room') || ''
  );

  const [username, setUsername] = useState(
    () => locationState?.username || socketService.getSessionInfo().username || ''
  );
  const [roomId, setRoomId] = useState(invitedRoomId);
  const [error, setError] = useState(locationState?.error || '');

  useEffect(() => {
    if (invitedRoomId) {
      setRoomId(invitedRoomId);
    }
  }, [invitedRoomId]);

  const handleJoinRoom = (e: React.FormEvent) => {
    e.preventDefault();

    const roomToJoin = socketService.normalizeRoomId(roomId);
    const usernameToUse = username.trim();

    if (!usernameToUse) {
      setError('Please enter a username');
      return;
    }

    if (usernameToUse.length > 10) {
      setError('Username must be 10 characters or less');
      return;
    }

    if (!roomToJoin) {
      setError('Please enter a room ID');
      return;
    }

    setError('');
    navigate(`/room/${roomToJoin}`, { state: { username: usernameToUse } });
  };

  const handleHomeRedirect = () => {
    navigate('/');
  };

  const hasInvite = Boolean(invitedRoomId);

  return (
    <div className="landing landing--compact">
      <section className="hero hero--compact">
        <span className="eyebrow">{hasInvite ? 'You’re invited' : 'Join a game'}</span>
        <h1 className="hero-title">{hasInvite ? 'Pick a name to join' : 'Got a room code?'}</h1>
        <p className="hero-subtitle">
          {hasInvite
            ? 'The room code is already in the link. Enter a username and jump in.'
            : 'Enter your name and the code your host shared to jump in.'}
        </p>
      </section>

      <ResumeSessionCard />

      <div className="panel auth-card">
        <form onSubmit={handleJoinRoom} noValidate>
          {error && (
            <div className="alert alert--error" role="alert">
              {error}
            </div>
          )}

          <div className="field">
            <label className="field-label" htmlFor="username">
              Your name
            </label>
            <input
              className="input"
              type="text"
              id="username"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError('');
              }}
              placeholder="Up to 10 characters"
              maxLength={10}
              autoComplete="nickname"
              autoFocus
            />
          </div>

          {hasInvite ? (
            <div className="field">
              <div className="field-label-row">
                <span className="field-label">Room</span>
                <Link to="/join" className="field-alt-link">
                  Use a different code
                </Link>
              </div>
              <div className="invited-room" aria-label={`Joining room ${roomId}`}>
                <span className="invited-room-label">Joining</span>
                <code>{roomId}</code>
              </div>
            </div>
          ) : (
            <div className="field">
              <label className="field-label" htmlFor="roomId">
                Room code
              </label>
              <input
                className="input input--code"
                type="text"
                id="roomId"
                value={roomId}
                onChange={(e) => {
                  setRoomId(e.target.value);
                  if (error) setError('');
                }}
                placeholder="e.g. k3x9pq"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
          )}

          <div className="cta-row">
            <button className="btn btn-primary btn-lg" type="submit">
              Join room
            </button>
            <button className="btn btn-secondary btn-lg" type="button" onClick={handleHomeRedirect}>
              Back
            </button>
          </div>
        </form>
      </div>

      <section className="about" aria-label="About Songle">
        <h2>Join a Songle room and start guessing</h2>
        <p>
          Open an invite link or enter a room ID to join a multiplayer song guessing game in your browser. You’ll hear
          short music clips and compete to guess the correct song title faster than your friends. Every room plays
          within a theme, and the songs you hear are found and submitted by the players to match that theme.
        </p>
        <p>
          The gameplay feels like <strong>skribbl.io</strong>: fast rounds, everyone guessing at the
          same time, and live scoring. The twist is that the “prompt” is music—so it’s perfect for quick music trivia
          nights and party games.
        </p>
      </section>
    </div>
  );
};

export default Join;
